import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, TooltipProps, Label } from 'recharts';
import { Empty } from '@frontend/app/refresh-components';
import { ChartPieSimpleIcon } from '@revfluence/fresh-icons/solid/esm';
import { ChartContainer, ChartTooltip } from '@/shadcn/components/ui/chart';
import type { GenderDistributionChartItem } from '../hooks'; // Changed import

interface GenderDistributionViewProps { // Changed interface name
  genderDistributionData?: GenderDistributionChartItem[]; // Changed prop name and type
}

// Tooltip Content - Type cast changed
const CustomChartTooltipContent = ({ active, payload }: TooltipProps<number, string>) => {
  if (active && payload && payload.length && payload[0].payload) {
    const data = payload[0].payload as GenderDistributionChartItem; // Changed type cast
    return (
      <div className="p-2 bg-background border text-foreground border-border rounded-md shadow-lg text-sm">
        <p className="label font-semibold">{`${data.name}`}</p>
        <p className="intro">{`Percentage: ${data.value}%`}</p>
      </div>
    );
  }
  return null;
};

export const GenderDistributionView: React.FC<GenderDistributionViewProps> = ({ genderDistributionData }) => { // Changed component name and prop
  const [activeSliceData, setActiveSliceData] = useState<GenderDistributionChartItem | null>(null); // Changed type

  useEffect(() => {
    if (genderDistributionData && genderDistributionData.length > 0) {
      // Find the segment with the largest value to make it active by default
      const largestSegment = genderDistributionData.reduce((max, item) => (item.value > max.value ? item : max), genderDistributionData[0]);
      setActiveSliceData(largestSegment);
    } else {
      setActiveSliceData(null);
    }
  }, [genderDistributionData]); // Changed dependency

  const handlePieMouseEnter = (_: any, index: number) => {
    if (genderDistributionData) {
      setActiveSliceData(genderDistributionData[index]); // Changed data source
    }
  };

  const handlePieMouseLeave = () => {
    // Revert to the largest segment when the mouse leaves
    if (genderDistributionData && genderDistributionData.length > 0) {
      const largestSegment = genderDistributionData.reduce((max, item) => (item.value > max.value ? item : max), genderDistributionData[0]);
      setActiveSliceData(largestSegment);
    } else {
      setActiveSliceData(null);
    }
  };

  if (!genderDistributionData || genderDistributionData.length === 0) { // Changed condition
    return (
      <Empty
        icon={ChartPieSimpleIcon}
        title="No Data Found."
        size="lg"
      />
    );
  }

  const chartConfig = {}; // Config can be extended if needed

  return (
    <ChartContainer config={chartConfig} className="w-full h-[250px]">
      <PieChart>
        <ChartTooltip content={<CustomChartTooltipContent />} />
        <Pie
          data={genderDistributionData} // Changed data source
          cx="50%"
          cy="50%"
          labelLine={false}
          outerRadius={120}
          innerRadius={70}
          fill="#8884d8" // Default fill, overridden by Cell
          dataKey="value"
          nameKey="name"
          onMouseEnter={handlePieMouseEnter}
          onMouseLeave={handlePieMouseLeave}
          paddingAngle={2}
          cornerRadius={4}
        >
          {genderDistributionData.map((entry) => ( // Changed data source
            <Cell
              key={`cell-${entry.key}`}
              fill={entry.fill}
              opacity={activeSliceData === null || activeSliceData?.key === entry.key ? 1 : 0.6}
            />
          ))}
          <Label
            content={({ viewBox }) => {
              if (activeSliceData && viewBox && 'cx' in viewBox && typeof viewBox.cx === 'number') {
                const { cx, cy } = viewBox;
                const displayText = activeSliceData.name;
                const displayValue = `${activeSliceData.value}%`;
                return (
                  <text x={cx} y={cy - 5} textAnchor="middle" dominantBaseline="middle" className="fill-foreground text-sm font-semibold">
                    {displayText}
                    <tspan x={cx} y={cy + 15} className="fill-muted-foreground text-xs">
                      {displayValue}
                    </tspan>
                  </text>
                );
              }
              return null;
            }}
            position="center"
          />
        </Pie>
      </PieChart>
    </ChartContainer>
  );
};

GenderDistributionView.displayName = 'GenderDistributionView';
